import {
  DIAS_MAX_ABERTURA_SEM_CNAE,
  MAX_IMPORTACAO_PROSPECCAO,
  MAX_VENDEDORES_ADICIONAIS,
  limparCnpj,
  validarCnpj,
  type PorteProspeccao,
  type ProspeccaoBusca,
} from '@crm/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal, Progresso, TagChip } from '../components/ui';
import { data, formatarWhatsapp, hojeISO, num } from '../format';
import { useTags, useUsuarios } from './Clientes';

// ----------------------------------------------------------------------------- tipos

type Situacao = 'NOVA' | 'NA_CARTEIRA' | 'NAO_CONTACTAR' | 'SEM_TELEFONE' | 'TELEFONE_EM_USO';
interface Empresa {
  cnpj: string; nome: string; razaoSocial: string | null; nomeFantasia: string | null; matriz: boolean;
  cnaePrincipal: string | null; cnaeDescricao: string | null; viaSecundaria?: boolean; uf: string;
  municipio: string | null; bairro: string | null; dataAbertura: string | null; porte: string | null;
  mei: boolean; celular: boolean; temEmail: boolean; whatsapp: string | null; situacao: Situacao; vendedorNome: string | null;
}
interface Resumo { total: number; comCelular: number; naCarteira: number; plano: { limite: number | null; usados: number } }
interface Resultado {
  data: Empresa[]; proximoCursor: string | null; limit: number; resumo: Resumo | null;
}
interface Detalhe extends Empresa {
  cnaesSecundarios: Array<{ codigo: string; descricao: string | null }>;
  endereco: string | null; cep: string | null; email: string | null;
  telefones: Array<{ numero: string; celular: boolean }>;
  capitalSocial: string | null;
  /** null = base carregada sem o quadro de sócios */
  socios: Array<{ nome: string; tipo: 'PF' | 'PJ' | 'ESTRANGEIRO' | null; qualificacao: string | null; desde: string | null }> | null;
  unidades: Array<{ cnpj: string; matriz: boolean; municipio: string | null; uf: string; bairro: string | null; naCarteira: boolean; vendedorNome: string | null }>;
}
interface StatusBase { disponivel: boolean; motivo?: string; referencia?: string | null; atualizadaEm?: string; estabelecimentos?: number }
interface Opcao { id: string; label: string; sub?: string }
interface ResultadoImportacao {
  importados: Array<{ cnpj: string; clienteId: string; nome: string; semWhatsapp: string | null }>;
  ignorados: Array<{ cnpj: string; nome: string | null; motivo: string; descricao: string }>;
  encontradas: number;
  /** Quantas entraram com vendedores que também atendem (specs/22). */
  comAdicionais?: number;
}

interface Filtros {
  /** Trecho da razão social ou do nome fantasia (exige município). */
  nome: string;
  cnaes: Opcao[];
  incluirSecundarios: boolean;
  ufs: string[];
  /** sub = UF do município (usado para descartar municípios quando a UF sai da seleção) */
  municipios: Opcao[];
  bairroOuCep: string;
  periodo: string;
  aberturaDe: string;
  aberturaAte: string;
  /** Faixa de capital social em R$, como digitado ("50.000" ou "1.000.000,00"); vazio = sem limite */
  capitalDe: string;
  capitalAte: string;
  portes: PorteProspeccao[];
  tipos: Array<'MATRIZ' | 'FILIAL'>;
  somenteCelular: boolean;
  somenteEmail: boolean;
  excluirMei: boolean;
  ocultarCarteira: boolean;
}

// ----------------------------------------------------------------------------- constantes

const UFS = ['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'];
const REGIOES: Array<{ nome: string; ufs: string[] }> = [
  { nome: 'Norte', ufs: ['AC', 'AM', 'AP', 'PA', 'RO', 'RR', 'TO'] },
  { nome: 'Nordeste', ufs: ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'] },
  { nome: 'Centro-Oeste', ufs: ['DF', 'GO', 'MS', 'MT'] },
  { nome: 'Sudeste', ufs: ['ES', 'MG', 'RJ', 'SP'] },
  { nome: 'Sul', ufs: ['PR', 'RS', 'SC'] },
];

const menosDias = (dias: number) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const PERIODOS: Array<{ id: string; label: string; dias: number | null }> = [
  { id: '30d', label: 'Últimos 30 dias', dias: 30 },
  { id: '90d', label: '90 dias', dias: 90 },
  { id: '6m', label: '6 meses', dias: 182 },
  { id: '12m', label: '12 meses', dias: 365 },
  { id: 'todos', label: 'Qualquer data', dias: null },
];

const SITUACAO: Record<Situacao, { label: string; cls: string }> = {
  NOVA: { label: 'Nova', cls: 'blue' },
  NA_CARTEIRA: { label: 'Na carteira', cls: '' },
  NAO_CONTACTAR: { label: 'Não contactar', cls: 'red' },
  SEM_TELEFONE: { label: 'Nova · sem telefone', cls: 'amber' },
  TELEFONE_EM_USO: { label: 'Nova · telefone já usado', cls: 'amber' },
};

/** Situações que podem ser importadas (sem telefone / telefone em uso entram sem WhatsApp). */
const IMPORTAVEL = new Set<Situacao>(['NOVA', 'SEM_TELEFONE', 'TELEFONE_EM_USO']);

type AlvoImportacao =
  | { tipo: 'cnpjs'; cnpjs: string[]; nomes: Map<string, string> }
  | { tipo: 'filtro'; filtros: Filtros; total: number };

const CHAVE_FILTROS = 'crm:prospeccao:filtros';
const FILTROS_PADRAO: Filtros = {
  nome: '', cnaes: [], incluirSecundarios: false, ufs: [], municipios: [], bairroOuCep: '', periodo: 'todos',
  aberturaDe: '', aberturaAte: '', capitalDe: '', capitalAte: '', portes: [], tipos: [], somenteCelular: true, somenteEmail: false, excluirMei: false, ocultarCarteira: true,
};

function lerFiltros(): Filtros {
  try {
    const s = localStorage.getItem(CHAVE_FILTROS);
    if (s) {
      // versão antiga gravava uma única UF em `uf`
      const { uf, ...salvo } = JSON.parse(s) as Partial<Filtros> & { uf?: string };
      const f: Filtros = { ...FILTROS_PADRAO, ...salvo };
      if (!Array.isArray(salvo.ufs)) {
        f.ufs = uf ? [uf] : [];
        f.municipios = (f.municipios ?? []).map((m) => ({ ...m, sub: m.sub ?? uf }));
      }
      return f;
    }
  } catch { /* storage indisponível */ }
  return FILTROS_PADRAO;
}

/** "1.000.000,50" / "50000" / "R$ 50 000" → número; vazio ou inválido → undefined */
const lerReais = (t: string): number | undefined => {
  const limpo = t.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  if (!limpo) return undefined;
  const n = Number(limpo);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};
const reaisFmt = (t: string) => {
  const n = lerReais(t);
  return n === undefined ? t : n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const cnpjFmt = (c: string) => `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
const cnaeFmt = (c: string | null) => (c ? `${c.slice(0, 4)}-${c.slice(4, 5)}/${c.slice(5)}` : '—');
const telFmt = (n: string) => formatarWhatsapp(`+55${n}`);

/** CNPJ digitado no campo de nome: abre a ficha direto (consulta instantânea, sem município). */
const cnpjDigitado = (t: string) => {
  const c = limparCnpj(t);
  return /^[\d.\/\s-]+$/.test(t.trim()) && c.length === 14 && validarCnpj(c) ? c : null;
};
/** Sem acento e em maiúsculas, letra a letra (mantém as posições para destacar o trecho). */
const normalizarLetras = (t: string) => Array.from(t, (ch) => ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '') || ch).join('').toUpperCase();

/** Destaca a palavra mais longa da busca por nome dentro do texto. */
function Destaque({ texto, busca }: { texto: string; busca: string | undefined }) {
  const palavra = (busca ? normalizarLetras(busca.trim()).split(/\s+/) : []).sort((a, b) => b.length - a.length)[0];
  const chars = Array.from(texto);
  const i = palavra && chars.length === normalizarLetras(texto).length ? normalizarLetras(texto).indexOf(palavra) : -1;
  if (!palavra || i < 0) return <>{texto}</>;
  return <>{chars.slice(0, i).join('')}<mark>{chars.slice(i, i + palavra.length).join('')}</mark>{chars.slice(i + palavra.length).join('')}</>;
}

function paraFiltros(f: Filtros) {
  const { cursor: _c, limit: _l, ...resto } = paraApi(f, undefined);
  return resto;
}

function paraApi(f: Filtros, cursor: string | undefined): ProspeccaoBusca {
  return {
    nome: f.nome.trim() || undefined,
    cnaes: f.cnaes.map((c) => c.id),
    incluirSecundarios: f.incluirSecundarios,
    ufs: f.ufs,
    municipios: f.municipios.map((m) => Number(m.id)),
    bairroOuCep: f.bairroOuCep.trim() || undefined,
    aberturaDe: f.aberturaDe || undefined,
    aberturaAte: f.aberturaAte || undefined,
    capitalDe: lerReais(f.capitalDe),
    capitalAte: lerReais(f.capitalAte),
    portes: f.portes,
    tipos: f.tipos,
    somenteCelular: f.somenteCelular,
    somenteEmail: f.somenteEmail,
    excluirMei: f.excluirMei,
    ocultarCarteira: f.ocultarCarteira,
    ordem: 'abertura',
    cursor,
    limit: 50,
  };
}

// ----------------------------------------------------------------------------- campo de chips com autocomplete

function ChipsAutocomplete({
  id, rotulo, itens, onAdd, onRemove, buscar, chave, placeholder, disabled, minimo = 2,
}: {
  id: string; rotulo: string; itens: Opcao[]; onAdd: (o: Opcao) => void; onRemove: (id: string) => void;
  buscar: (q: string) => Promise<Opcao[]>; chave: unknown[]; placeholder: string; disabled?: boolean; minimo?: number;
}) {
  const [texto, setTexto] = useState('');
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const listaId = useId();
  useEffect(() => {
    const t = setTimeout(() => setTermo(texto.trim()), 250);
    return () => clearTimeout(t);
  }, [texto]);
  const q = useQuery({
    queryKey: [...chave, termo],
    queryFn: () => buscar(termo),
    enabled: !disabled && termo.length >= minimo,
    staleTime: 5 * 60_000,
  });
  const opcoes = (q.data ?? []).filter((o) => !itens.some((i) => i.id === o.id));
  const escolher = (o: Opcao) => {
    onAdd(o);
    setTexto('');
    setTermo('');
    setAtivo(0);
  };
  const mostrar = aberto && termo.length >= minimo && (opcoes.length > 0 || q.isFetched);

  return (
    <div className="ac">
      <div className={`chips-input${disabled ? ' disabled' : ''}`}>
        {itens.map((i) => (
          <span key={i.id} className="chip" title={i.sub ?? i.label}>
            <span className="chip-txt">{i.label}</span>
            <button type="button" aria-label={`Remover ${i.label}`} onClick={() => onRemove(i.id)}>×</button>
          </span>
        ))}
        <input
          id={id}
          role="combobox"
          aria-label={rotulo}
          aria-expanded={mostrar}
          aria-controls={listaId}
          aria-autocomplete="list"
          disabled={disabled}
          value={texto}
          placeholder={itens.length ? '' : placeholder}
          onChange={(e) => { setTexto(e.target.value); setAberto(true); setAtivo(0); }}
          onFocus={() => setAberto(true)}
          onBlur={() => setTimeout(() => setAberto(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setAtivo((a) => Math.min(a + 1, opcoes.length - 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setAtivo((a) => Math.max(a - 1, 0)); }
            else if (e.key === 'Enter' && mostrar && opcoes[ativo]) { e.preventDefault(); escolher(opcoes[ativo]!); }
            else if (e.key === 'Escape') setAberto(false);
            else if (e.key === 'Backspace' && !texto && itens.length) onRemove(itens[itens.length - 1]!.id);
          }}
        />
      </div>
      {mostrar && (
        <ul className="ac-lista" id={listaId} role="listbox">
          {opcoes.length === 0 && <li className="ac-vazio">{q.isFetching ? 'Buscando…' : 'Nada encontrado.'}</li>}
          {opcoes.map((o, i) => (
            <li
              key={o.id}
              role="option"
              aria-selected={i === ativo}
              className={i === ativo ? 'on' : undefined}
              onMouseDown={(e) => { e.preventDefault(); escolher(o); }}
              onMouseEnter={() => setAtivo(i)}
            >
              <span>{o.label}</span>
              {o.sub && <span className="small faint">{o.sub}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------- UF (multiseleção)

function UfMultiSelect({ valor, onChange, disabled }: { valor: string[]; onChange: (ufs: string[]) => void; disabled?: boolean }) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const painelId = useId();
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => { if (!raiz.current?.contains(e.target as Node)) setAberto(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fora); document.removeEventListener('keydown', esc); };
  }, [aberto]);

  const ordenar = (l: string[]) => UFS.filter((u) => l.includes(u));
  const alternarUf = (u: string) => onChange(ordenar(valor.includes(u) ? valor.filter((x) => x !== u) : [...valor, u]));
  const regiaoCompleta = (r: string[]) => r.every((u) => valor.includes(u));
  const alternarRegiao = (r: string[]) =>
    onChange(ordenar(regiaoCompleta(r) ? valor.filter((u) => !r.includes(u)) : [...new Set([...valor, ...r])]));
  const resumo = !valor.length ? 'UF' : valor.length === UFS.length ? 'Brasil' : valor.length <= 2 ? valor.join(', ') : `${valor.length} UFs`;

  return (
    <div className="uf-multi" ref={raiz}>
      <button
        type="button"
        className={`select uf-multi-btn${valor.length ? '' : ' vazio'}`}
        aria-label={`UF: ${valor.length ? valor.join(', ') : 'nenhuma'}`}
        aria-haspopup="true"
        aria-expanded={aberto}
        aria-controls={painelId}
        title={valor.join(', ') || undefined}
        disabled={disabled}
        onClick={() => setAberto((a) => !a)}
      >
        <span>{resumo}</span>
        <Icon name="abaixo" size={14} />
      </button>
      {aberto && (
        <div className="uf-multi-painel" id={painelId} role="group" aria-label="Estados">
          <div className="pills">
            {REGIOES.map((r) => (
              <button key={r.nome} type="button" className={`pill${regiaoCompleta(r.ufs) ? ' on' : ''}`} aria-pressed={regiaoCompleta(r.ufs)} onClick={() => alternarRegiao(r.ufs)}>
                {r.nome}
              </button>
            ))}
          </div>
          <div className="uf-multi-grade">
            {UFS.map((u) => (
              <label key={u} className="check small"><input type="checkbox" checked={valor.includes(u)} onChange={() => alternarUf(u)} />{u}</label>
            ))}
          </div>
          <div className="uf-multi-rodape">
            <button type="button" className="btn sm" disabled={valor.length === UFS.length} onClick={() => onChange([...UFS])}>Todas</button>
            <button type="button" className="btn sm" disabled={!valor.length} onClick={() => onChange([])}>Limpar</button>
            <button type="button" className="btn primary sm" onClick={() => setAberto(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------- página

export function ProspeccaoPage() {
  const { pode } = useAuth();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['prospeccao', 'status'], queryFn: () => api<StatusBase>('/prospeccao/status'), staleTime: 5 * 60_000 });

  const [f, setF] = useState<Filtros>(lerFiltros);
  const [consulta, setConsulta] = useState<Filtros | null>(null);
  // paginação por cursor: pilha dos cursores das páginas já visitadas
  const [pilha, setPilha] = useState<string[]>([]);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [erroForm, setErroForm] = useState<string | null>(null);
  const [selecao, setSelecao] = useState<Map<string, string>>(new Map());
  const [detalhe, setDetalhe] = useState<string | null>(null);
  const [importar, setImportar] = useState<AlvoImportacao | null>(null);
  /** "Selecionar todas do filtro": importa tudo que a busca encontra, sem marcar uma a uma */
  const [todasDoFiltro, setTodasDoFiltro] = useState(false);
  const set = <K extends keyof Filtros>(k: K, v: Filtros[K]) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    try { localStorage.setItem(CHAVE_FILTROS, JSON.stringify(f)); } catch { /* ignora */ }
  }, [f]);

  const busca = useQuery({
    queryKey: ['prospeccao', 'busca', consulta, pilha],
    queryFn: () => api<Resultado>('/prospeccao/busca', { body: paraApi(consulta!, pilha[pilha.length - 1]) }),
    enabled: !!consulta,
    placeholderData: keepPreviousData,
  });

  const buscar = (e?: FormEvent) => {
    e?.preventDefault();
    const doc = cnpjDigitado(f.nome);
    if (doc) {
      setErroForm(null);
      setDetalhe(doc);
      return;
    }
    const nome = f.nome.trim();
    if (nome && nome.length < 3) return setErroForm('Digite ao menos 3 letras do nome.');
    if (!f.ufs.length) return setErroForm('Escolha ao menos uma UF.');
    // CNAE é opcional; sem ele (e sem nome/município) só um período curto de abertura: um estado inteiro são milhões de empresas
    if (!f.cnaes.length && !nome && !f.municipios.length) {
      const dias = f.aberturaDe ? Math.round(((f.aberturaAte ? Date.parse(f.aberturaAte) : Date.parse(hojeISO())) - Date.parse(f.aberturaDe)) / 86_400_000) : Infinity;
      if (dias > DIAS_MAX_ABERTURA_SEM_CNAE) return setErroForm('Sem atividade (CNAE) e sem município, escolha um período de abertura de até 90 dias (ou um município, ou o nome da empresa).');
    }
    if (nome && !f.municipios.length) return setErroForm('Para buscar pelo nome, escolha ao menos um município.');
    if (f.aberturaDe && f.aberturaAte && f.aberturaDe > f.aberturaAte) return setErroForm('A data inicial é depois da final.');
    const capDe = lerReais(f.capitalDe);
    const capAte = lerReais(f.capitalAte);
    if ((f.capitalDe.trim() && capDe === undefined) || (f.capitalAte.trim() && capAte === undefined)) return setErroForm('Capital social inválido. Use só números, ex.: 50.000.');
    if (capDe !== undefined && capAte !== undefined && capDe > capAte) return setErroForm('O capital social mínimo é maior que o máximo.');
    setErroForm(null);
    setPilha([]);
    setResumo(null);
    setConsulta({ ...f });
    setTodasDoFiltro(false);
    setSelecao(new Map());
  };

  const escolherPeriodo = (id: string) => {
    const p = PERIODOS.find((x) => x.id === id)!;
    setF((x) => ({ ...x, periodo: id, aberturaDe: p.dias ? menosDias(p.dias) : '', aberturaAte: p.dias ? hojeISO() : '' }));
  };
  const alternar = <T,>(lista: T[], v: T) => (lista.includes(v) ? lista.filter((x) => x !== v) : [...lista, v]);

  const r = busca.data;
  useEffect(() => {
    if (busca.data?.resumo) setResumo(busca.data.resumo);
  }, [busca.data]);
  const selecionaveis = r?.data.filter((e) => IMPORTAVEL.has(e.situacao)) ?? [];
  const todasMarcadas = selecionaveis.length > 0 && selecionaveis.every((e) => selecao.has(e.cnpj));
  const livres = resumo?.plano.limite != null ? Math.max(0, resumo.plano.limite - resumo.plano.usados) : null;
  const pagina = pilha.length + 1;
  const paginas = resumo && r ? Math.max(1, Math.ceil(resumo.total / r.limit)) : null;

  const marcar = (e: Empresa, on: boolean) =>
    setSelecao((s) => {
      const n = new Map(s);
      if (on) n.set(e.cnpj, e.nome); else n.delete(e.cnpj);
      return n;
    });
  const marcarPagina = (on: boolean) =>
    setSelecao((s) => {
      const n = new Map(s);
      for (const e of selecionaveis) if (on) n.set(e.cnpj, e.nome); else n.delete(e.cnpj);
      return n;
    });

  const aoImportar = (res: ResultadoImportacao) => {
    setTodasDoFiltro(false);
    setSelecao((s) => {
      const n = new Map(s);
      for (const i of [...res.importados, ...res.ignorados]) n.delete(i.cnpj);
      return n;
    });
    qc.invalidateQueries({ queryKey: ['prospeccao', 'busca'] });
    qc.invalidateQueries({ queryKey: ['prospeccao', 'detalhe'] });
    qc.invalidateQueries({ queryKey: ['clientes'] });
  };

  const indisponivel = status.data && !status.data.disponivel;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Prospecção</h1>
          <span className="muted">Encontre empresas ativas na base pública de CNPJ e traga para a sua carteira.</span>
        </div>
      </div>

      {indisponivel && (
        <div className="alert warn" role="status">
          <Icon name="alerta" />
          <span>
            {status.data!.motivo === 'SEM_CARGA'
              ? 'A base de CNPJ ainda não foi carregada neste servidor. Peça ao responsável técnico para rodar a carga da Receita.'
              : 'A base de CNPJ não está disponível neste servidor no momento.'}
          </span>
        </div>
      )}

      <form className="card" onSubmit={buscar} aria-label="Filtros da prospecção">
        <div className="field">
          <label htmlFor="pros-nome"><span>Nome da empresa</span></label>
          <input
            id="pros-nome"
            className="input"
            placeholder="Razão social, nome fantasia ou CNPJ (opcional)"
            value={f.nome}
            maxLength={80}
            autoComplete="off"
            disabled={indisponivel}
            onChange={(e) => set('nome', e.target.value)}
          />
          <span className="hint">
            {cnpjDigitado(f.nome)
              ? 'CNPJ: ao buscar, abre a ficha da empresa direto.'
              : f.nome.trim()
                ? 'Procura no nome fantasia e na razão social, sem acento. Precisa de ao menos um município; a atividade fica opcional.'
                : 'Use para achar uma empresa específica na sua região.'}
          </span>
        </div>
        <div className="filtros-grid">
          <div className="field">
            <label htmlFor="pros-cnae"><span>Atividade (CNAE) <span className="faint">· opcional</span></span></label>
            <ChipsAutocomplete
              id="pros-cnae"
              rotulo="Atividade (CNAE)"
              itens={f.cnaes}
              chave={['prospeccao', 'cnaes']}
              placeholder="Código ou descrição, ex.: farmácia"
              disabled={indisponivel}
              buscar={async (q) =>
                (await api<Array<{ codigo: string; descricao: string }>>(`/prospeccao/cnaes?q=${encodeURIComponent(q)}`)).map((c) => ({
                  id: c.codigo, label: `${cnaeFmt(c.codigo)} ${c.descricao}`, sub: undefined,
                }))
              }
              onAdd={(o) => f.cnaes.length < 20 && set('cnaes', [...f.cnaes, o])}
              onRemove={(id) => set('cnaes', f.cnaes.filter((c) => c.id !== id))}
            />
            <label className="check small"><input type="checkbox" checked={f.incluirSecundarios} disabled={!f.cnaes.length} onChange={(e) => set('incluirSecundarios', e.target.checked)} />Incluir empresas com esse CNAE como atividade secundária</label>
          </div>

          <div className="field">
            <span>Localização</span>
            <div className="uf-mun">
              <UfMultiSelect
                valor={f.ufs}
                disabled={indisponivel}
                onChange={(ufs) =>
                  // município cuja UF saiu da seleção deixa de valer
                  setF((x) => ({ ...x, ufs, municipios: x.municipios.filter((m) => !m.sub || ufs.includes(m.sub)) }))
                }
              />
              <ChipsAutocomplete
                id="pros-mun"
                rotulo="Municípios"
                itens={f.municipios}
                chave={['prospeccao', 'municipios', [...f.ufs].sort().join(',')]}
                placeholder={f.ufs.length ? (f.nome.trim() && !cnpjDigitado(f.nome) ? 'Município (obrigatório com nome)' : 'Município (opcional)') : 'Escolha a UF'}
                disabled={!f.ufs.length || indisponivel}
                buscar={async (q) =>
                  (await api<Array<{ codigo: number; nome: string; uf: string }>>(
                    `/prospeccao/municipios?uf=${f.ufs.join(',')}&q=${encodeURIComponent(q)}`,
                  )).map((m) => ({ id: String(m.codigo), label: `${m.nome}/${m.uf}`, sub: m.uf }))
                }
                onAdd={(o) => f.municipios.length < 50 && set('municipios', [...f.municipios, o])}
                onRemove={(id) => set('municipios', f.municipios.filter((m) => m.id !== id))}
              />
            </div>
            <input className="input" aria-label="Bairro ou CEP" placeholder="Bairro ou início do CEP (opcional)" value={f.bairroOuCep} maxLength={80} onChange={(e) => set('bairroOuCep', e.target.value)} />
          </div>

          <div className="field">
            <span>Data de abertura da empresa</span>
            <div className="pills" role="group" aria-label="Período de abertura">
              {PERIODOS.map((p) => (
                <button key={p.id} type="button" className={`pill${f.periodo === p.id ? ' on' : ''}`} aria-pressed={f.periodo === p.id} onClick={() => escolherPeriodo(p.id)}>{p.label}</button>
              ))}
            </div>
            <div className="datas">
              <label className="field"><span>De</span><input className="input" type="date" value={f.aberturaDe} max={f.aberturaAte || undefined} onChange={(e) => setF((x) => ({ ...x, aberturaDe: e.target.value, periodo: 'personalizado' }))} /></label>
              <label className="field"><span>Até</span><input className="input" type="date" value={f.aberturaAte} min={f.aberturaDe || undefined} onChange={(e) => setF((x) => ({ ...x, aberturaAte: e.target.value, periodo: 'personalizado' }))} /></label>
            </div>
            <span className="hint">Empresas recém-abertas ainda não têm fornecedor fixo.{!f.cnaes.length && !f.municipios.length && !f.nome.trim() ? ' Sem atividade e sem município: até 90 dias.' : ''}</span>
          </div>
        </div>

        <div className="filtros-linha">
          <div className="pills" role="group" aria-label="Porte">
            <span className="small muted">Porte</span>
            {(['ME', 'EPP', 'DEMAIS'] as const).map((p) => (
              <button key={p} type="button" className={`pill${f.portes.includes(p) ? ' on' : ''}`} aria-pressed={f.portes.includes(p)} onClick={() => set('portes', alternar(f.portes, p))}>
                {p === 'DEMAIS' ? 'Demais' : p}
              </button>
            ))}
          </div>
          <div className="pills" role="group" aria-label="Tipo de unidade">
            <span className="small muted">Tipo</span>
            {(['MATRIZ', 'FILIAL'] as const).map((t) => (
              <button key={t} type="button" className={`pill${f.tipos.includes(t) ? ' on' : ''}`} aria-pressed={f.tipos.includes(t)} onClick={() => set('tipos', alternar(f.tipos, t))}>
                {t === 'MATRIZ' ? 'Matriz' : 'Filial'}
              </button>
            ))}
          </div>
          <div className="capital" role="group" aria-label="Capital social">
            <span className="small muted">Capital social R$</span>
            <input className="input num" inputMode="decimal" placeholder="de" aria-label="Capital social mínimo" value={f.capitalDe} maxLength={20}
              onChange={(e) => set('capitalDe', e.target.value)} onBlur={(e) => set('capitalDe', reaisFmt(e.target.value))} />
            <span className="small faint">a</span>
            <input className="input num" inputMode="decimal" placeholder="até" aria-label="Capital social máximo" value={f.capitalAte} maxLength={20}
              onChange={(e) => set('capitalAte', e.target.value)} onBlur={(e) => set('capitalAte', reaisFmt(e.target.value))} />
          </div>
          <label className="check"><input type="checkbox" checked={f.somenteCelular} onChange={(e) => set('somenteCelular', e.target.checked)} />Só com celular (WhatsApp provável)</label>
          <label className="check"><input type="checkbox" checked={f.somenteEmail} onChange={(e) => set('somenteEmail', e.target.checked)} />Só com e-mail</label>
          <label className="check"><input type="checkbox" checked={f.excluirMei} onChange={(e) => set('excluirMei', e.target.checked)} />Excluir MEI</label>
          <label className="check"><input type="checkbox" checked={f.ocultarCarteira} onChange={(e) => set('ocultarCarteira', e.target.checked)} />Ocultar quem já está na carteira</label>
          <button className="btn primary" type="submit" style={{ marginLeft: 'auto' }} disabled={indisponivel || busca.isFetching}>
            <Icon name="busca" size={15} />{busca.isFetching ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
        <Erro erro={erroForm} />
      </form>

      {busca.error && <Erro erro={mensagemErro(busca.error)} />}

      {resumo && (
        <div className="grid-kpi">
          <div className="card kpi"><span className="label">Empresas encontradas</span><span className="value">{num(resumo.total)}</span></div>
          <div className="card kpi"><span className="label">Com celular</span><span className="value">{num(resumo.comCelular)}</span></div>
          <div className="card kpi"><span className="label">Já na sua carteira{consulta?.ocultarCarteira ? ' (ocultas)' : ''}</span><span className="value">{num(resumo.naCarteira)}</span></div>
          <div className="card kpi">
            <span className="label">Espaço no plano</span>
            <span className="value">{livres === null ? 'Ilimitado' : num(livres)}{livres !== null && <span className="small faint" style={{ fontFamily: 'var(--font)', fontWeight: 400 }}> clientes livres</span>}</span>
          </div>
        </div>
      )}

      {consulta && (
        <section className="card flush" aria-label="Resultados">
          {todasDoFiltro && resumo ? (
            <div className="selecao-bar">
              <span>Todas as <strong className="num">{num(resumo.total)}</strong> empresas do filtro selecionadas</span>
              <div className="actions">
                <button className="btn sm" onClick={() => { setTodasDoFiltro(false); setSelecao(new Map()); }}>Limpar seleção</button>
                <button className="btn sm primary" onClick={() => setImportar({ tipo: 'filtro', filtros: consulta!, total: resumo.total })}>Importar para a carteira</button>
              </div>
            </div>
          ) : selecao.size > 0 && (
            <div className="selecao-bar">
              <span><strong className="num">{num(selecao.size)}</strong> selecionada{selecao.size > 1 ? 's' : ''}</span>
              {resumo && todasMarcadas && resumo.total > selecao.size && (
                <button type="button" className="link-btn" style={{ color: 'var(--link)' }} onClick={() => setTodasDoFiltro(true)}>
                  Selecionar todas as {num(resumo.total)} do filtro
                </button>
              )}
              {selecao.size > MAX_IMPORTACAO_PROSPECCAO && <span className="small" style={{ color: 'var(--amber-text)' }}>Importe no máximo {num(MAX_IMPORTACAO_PROSPECCAO)} por vez.</span>}
              <div className="actions">
                <button className="btn sm" onClick={() => setSelecao(new Map())}>Limpar seleção</button>
                <button className="btn sm primary" disabled={selecao.size > MAX_IMPORTACAO_PROSPECCAO} onClick={() => setImportar({ tipo: 'cnpjs', cnpjs: [...selecao.keys()], nomes: selecao })}>Importar para a carteira</button>
              </div>
            </div>
          )}
          {busca.isLoading ? <Carregando /> : !r || r.data.length === 0 ? (
            <div className="empty">Nenhuma empresa encontrada com esses filtros.</div>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input type="checkbox" aria-label="Selecionar as empresas novas desta página" checked={todasDoFiltro || todasMarcadas} disabled={todasDoFiltro || !selecionaveis.length} onChange={(e) => marcarPagina(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
                      </th>
                      <th>Empresa</th><th>Atividade</th><th>Local</th><th>Abertura</th><th>Contato</th><th>Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.data.map((e) => {
                      const s = SITUACAO[e.situacao];
                      return (
                        <tr key={e.cnpj} className="click" onClick={() => setDetalhe(e.cnpj)}>
                          <td onClick={(ev) => ev.stopPropagation()}>
                            <input type="checkbox" aria-label={`Selecionar ${e.nome}`} disabled={todasDoFiltro || !IMPORTAVEL.has(e.situacao)} checked={(todasDoFiltro && IMPORTAVEL.has(e.situacao)) || selecao.has(e.cnpj)} onChange={(ev) => marcar(e, ev.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--blue)' }} />
                          </td>
                          <td>
                            <div className="duas-linhas">
                              <button type="button" className="link-btn" onClick={(ev) => { ev.stopPropagation(); setDetalhe(e.cnpj); }}><Destaque texto={e.nome} busca={consulta?.nome} /></button>
                              <span className="small faint">{e.razaoSocial && e.razaoSocial !== e.nome ? <><Destaque texto={e.razaoSocial} busca={consulta?.nome} />{' · '}</> : ''}<span className="num">{cnpjFmt(e.cnpj)}</span>{e.matriz ? '' : ' · filial'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="duas-linhas" title={e.cnaeDescricao ?? undefined}>
                              <span className="num small">{cnaeFmt(e.cnaePrincipal)}</span>
                              <span className="small faint">{e.viaSecundaria ? 'via atividade secundária' : [e.porte, e.mei ? 'MEI' : null].filter(Boolean).join(' · ')}</span>
                            </div>
                          </td>
                          <td><div className="duas-linhas"><span>{e.municipio ?? '—'}/{e.uf}</span><span className="small faint">{e.bairro ?? ''}</span></div></td>
                          <td className="num small">{e.dataAbertura ? data(e.dataAbertura).slice(3) : '—'}</td>
                          <td>
                            <span style={{ display: 'flex', gap: 6 }}>
                              {e.whatsapp ? <span className={`badge ${e.celular ? 'green' : 'amber'}`}>{e.celular ? 'celular' : 'só fixo'}</span> : <span className="badge">sem telefone</span>}
                              {e.temEmail && <span className="badge">e-mail</span>}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${s.cls}`} title={e.vendedorNome ? `Vendedor: ${e.vendedorNome}` : undefined}>{s.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="pager">
                <span className="num">Página {num(pagina)}{paginas ? ` de ${num(paginas)}` : ''}</span>
                <div className="actions">
                  <button className="btn sm" disabled={pagina <= 1 || busca.isFetching} onClick={() => setPilha(pilha.slice(0, -1))}>Anterior</button>
                  <button className="btn sm" disabled={!r.proximoCursor || busca.isFetching} onClick={() => setPilha([...pilha, r.proximoCursor!])}>Próxima</button>
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {status.data?.disponivel && (
        <p className="small faint" style={{ margin: 0 }}>
          Fonte: base pública de CNPJ da Receita Federal, somente empresas ativas
          {status.data.referencia ? ` · referência ${status.data.referencia}` : ''} · carregada em {data(status.data.atualizadaEm)}.
          Telefone e e-mail são os declarados no cadastro da empresa.
        </p>
      )}

      {detalhe && (
        <PainelDetalhe
          cnpj={detalhe}
          onClose={() => setDetalhe(null)}
          onAbrir={setDetalhe}
          onImportar={(c, nome) => setImportar({ tipo: 'cnpjs', cnpjs: [c], nomes: new Map([[c, nome]]) })}
        />
      )}
      {importar && (
        <ModalImportar
          alvo={importar}
          livres={livres}
          podeDistribuir={pode('CLIENTES_ATRIBUIR')}
          onClose={() => setImportar(null)}
          onConcluido={aoImportar}
        />
      )}
    </>
  );
}

// ----------------------------------------------------------------------------- painel de detalhe

function PainelDetalhe({ cnpj, onClose, onAbrir, onImportar }: { cnpj: string; onClose: () => void; onAbrir: (c: string) => void; onImportar: (c: string, nome: string) => void }) {
  const q = useQuery({ queryKey: ['prospeccao', 'detalhe', cnpj], queryFn: () => api<Detalhe>(`/prospeccao/empresas/${cnpj}`) });
  const fechar = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    fechar.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const e = q.data;

  return (
    <div className="overlay drawer-overlay" onMouseDown={(ev) => ev.target === ev.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label="Detalhe da empresa">
        <header className="drawer-head">
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div className="duas-linhas" style={{ minWidth: 0 }}>
              <h2 style={{ fontSize: 19 }}>{e?.nome ?? 'Carregando…'}</h2>
              {e?.razaoSocial && e.razaoSocial !== e.nome && <span className="small muted">{e.razaoSocial}</span>}
            </div>
            <button ref={fechar} className="btn icon-btn" style={{ marginLeft: 'auto', flex: 'none' }} aria-label="Fechar" onClick={onClose}>×</button>
          </div>
          {e && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span className="badge green">ATIVA</span>
              <span className="badge">{e.matriz ? 'Matriz' : 'Filial'}</span>
              {e.porte && <span className="badge">{e.porte}</span>}
              <span className={`badge ${SITUACAO[e.situacao].cls}`}>{SITUACAO[e.situacao].label}{e.vendedorNome ? ` · ${e.vendedorNome}` : ''}</span>
            </div>
          )}
        </header>

        <div className="drawer-body">
          {q.isLoading && <Carregando />}
          {q.error && <Erro erro={mensagemErro(q.error)} />}
          {e && (
            <>
              <section className="col" style={{ gap: 8 }}>
                <h3 className="drawer-h3">Cadastro</h3>
                <dl className="dados dados-2">
                  <div><dt>CNPJ</dt><dd className="num">{cnpjFmt(e.cnpj)}</dd></div>
                  <div><dt>Abertura</dt><dd className="num">{data(e.dataAbertura)}</dd></div>
                  {e.capitalSocial && <div><dt>Capital social</dt><dd className="num">{Number(e.capitalSocial).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</dd></div>}
                </dl>
              </section>

              <section className="col" style={{ gap: 8 }}>
                <h3 className="drawer-h3">Sócios e administradores</h3>
                {e.socios === null ? (
                  <span className="small muted">A base carregada não tem o quadro de sócios. Ele entra na próxima carga do mês (zips de Sócios).</span>
                ) : e.socios.length === 0 ? (
                  <span className="small muted">Nenhum sócio informado na Receita{e.mei ? ' (MEI: o titular é o próprio nome da empresa)' : ''}.</span>
                ) : (
                  <ul className="lista-socios">
                    {e.socios.map((s) => (
                      <li key={`${s.nome}-${s.desde}`}>
                        <span className="nome">{s.nome}{s.tipo === 'PJ' && <span className="badge" style={{ marginLeft: 6 }}>empresa</span>}</span>
                        <span className="small muted">{s.qualificacao ?? 'Sócio'}{s.desde ? ` · desde ${s.desde.slice(0, 4)}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {e.socios && e.socios.length > 0 && <span className="small faint">Ao importar, os principais nomes vão para as observações do cliente.</span>}
              </section>

              <section className="col" style={{ gap: 8 }}>
                <h3 className="drawer-h3">Atividades</h3>
                <div className="mini"><span className="l">Principal</span><span><span className="num" style={{ color: 'var(--blue-text)' }}>{cnaeFmt(e.cnaePrincipal)}</span> {e.cnaeDescricao}</span></div>
                {e.cnaesSecundarios.length > 0 && (
                  <div className="mini">
                    <span className="l">Secundárias ({e.cnaesSecundarios.length})</span>
                    {e.cnaesSecundarios.map((c) => <span key={c.codigo}><span className="num">{cnaeFmt(c.codigo)}</span> {c.descricao ?? ''}</span>)}
                  </div>
                )}
              </section>

              <section className="col" style={{ gap: 8 }}>
                <h3 className="drawer-h3">Endereço</h3>
                <p style={{ margin: 0, color: 'var(--text-2)' }}>
                  {e.endereco ?? '—'}{e.bairro ? ` — ${e.bairro}` : ''}<br />
                  {e.municipio}/{e.uf}{e.cep ? <> · CEP <span className="num">{e.cep.slice(0, 5)}-{e.cep.slice(5)}</span></> : null}
                </p>
              </section>

              <section className="col" style={{ gap: 8 }}>
                <h3 className="drawer-h3">Contato declarado na Receita</h3>
                {e.telefones.length === 0 && <span className="muted">Sem telefone cadastrado.</span>}
                {e.telefones.map((t) => (
                  <div key={t.numero} className="mini" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <span className="num">{telFmt(t.numero)}</span>
                    <span className={`badge ${t.celular ? 'green' : ''}`}>{t.celular ? 'celular · WhatsApp provável' : 'fixo'}</span>
                  </div>
                ))}
                {e.email && <div className="mini"><span style={{ overflowWrap: 'anywhere' }}>{e.email}</span></div>}
                <span className="small faint">Muitas empresas declaram o telefone do contador. Confirme no primeiro contato.</span>
              </section>

              {e.unidades.length > 0 && (
                <section className="col" style={{ gap: 8 }}>
                  <h3 className="drawer-h3">Outras unidades do mesmo CNPJ base</h3>
                  <div className="lista-unidades">
                    {e.unidades.map((u) => (
                      <button key={u.cnpj} type="button" onClick={() => onAbrir(u.cnpj)}>
                        <span>{u.matriz ? 'Matriz' : `Filial ${u.cnpj.slice(8, 12)}`} · {u.municipio ?? '—'}/{u.uf}</span>
                        <span className={`badge ${u.naCarteira ? '' : 'blue'}`} style={{ marginLeft: 'auto' }}>
                          {u.naCarteira ? `Na carteira${u.vendedorNome ? ` · ${u.vendedorNome}` : ''}` : 'Nova'}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {e && (
          <footer className="drawer-foot">
            {IMPORTAVEL.has(e.situacao) ? (
              <>
                <button className="btn primary block lg" onClick={() => onImportar(e.cnpj, e.nome)}>Importar para a carteira</button>
                <span className="small faint" style={{ textAlign: 'center' }}>
                  {e.situacao === 'NOVA' && 'O WhatsApp é liberado depois de importar, para o contato entrar no histórico.'}
                  {e.situacao === 'SEM_TELEFONE' && 'Sem telefone válido na Receita: entra sem WhatsApp e você completa na ficha.'}
                  {e.situacao === 'TELEFONE_EM_USO' && `O telefone já é de outro cliente${e.vendedorNome ? ` (${e.vendedorNome})` : ''}: entra sem WhatsApp e você completa na ficha.`}
                </span>
              </>
            ) : (
              <span className="small muted" style={{ textAlign: 'center' }}>
                {e.situacao === 'NA_CARTEIRA' && `Esta empresa já está na carteira${e.vendedorNome ? ` de ${e.vendedorNome}` : ''}.`}
                {e.situacao === 'NAO_CONTACTAR' && 'Este telefone pediu para não ser contatado (LGPD).'}
              </span>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}

// ----------------------------------------------------------------------------- modal de importação

function ModalImportar({
  alvo, livres, podeDistribuir, onClose, onConcluido,
}: {
  alvo: AlvoImportacao; livres: number | null; podeDistribuir: boolean;
  onClose: () => void; onConcluido: (r: ResultadoImportacao) => void;
}) {
  const { me } = useAuth();
  const tags = useTags();
  const usuarios = useUsuarios(podeDistribuir);
  const ativos = useMemo(() => (usuarios.data ?? []).filter((u) => u.status === 'ATIVO'), [usuarios.data]);
  const [distribuicao, setDistribuicao] = useState<'EU' | 'RODIZIO'>('EU');
  const [vendedores, setVendedores] = useState<string[] | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [adicionais, setAdicionais] = useState<string[]>([]);
  const marcados = vendedores ?? ativos.filter((u) => u.perfil === 'VENDEDOR').map((u) => u.id);
  const nomeDe = (id: string) => ativos.find((u) => u.id === id)?.nome ?? me?.usuario.nome ?? '—';
  const pedidas = alvo.tipo === 'cnpjs' ? alvo.cnpjs.length : alvo.total;
  const n = livres === null ? pedidas : Math.min(pedidas, livres);

  const m = useMutation({
    mutationFn: () =>
      api<ResultadoImportacao>('/prospeccao/importar', {
        body: {
          ...(alvo.tipo === 'cnpjs' ? { cnpjs: alvo.cnpjs } : { filtros: paraFiltros(alvo.filtros) }),
          distribuicao, vendedorIds: distribuicao === 'RODIZIO' ? marcados : [], tagIds,
          vendedoresAdicionais: podeDistribuir ? adicionais : [],
        },
      }),
    onSuccess: onConcluido,
  });

  if (m.data) {
    const res = m.data;
    const semWhats = res.importados.filter((i) => i.semWhatsapp).length;
    return (
      <Modal titulo="Importação concluída" onClose={onClose}>
        <h2 style={{ fontSize: 18 }}>
          {res.importados.length ? `${num(res.importados.length)} empresa${res.importados.length > 1 ? 's' : ''} na carteira` : 'Nenhuma empresa importada'}
        </h2>
        {res.importados.length > 0 && <span className="muted small">Entraram como Prospect, com a origem LGPD registrada em cada cliente.</span>}
        {(res.comAdicionais ?? 0) > 0 && <span className="small">{num(res.comAdicionais!)} {res.comAdicionais === 1 ? 'entrou' : 'entraram'} também com {adicionais.map(nomeDe).join(', ')} atendendo.</span>}
        {semWhats > 0 && (
          <div className="alert info" style={{ flexDirection: 'column', gap: 6 }}>
            <span><strong>{num(semWhats)}</strong> {semWhats > 1 ? 'entraram' : 'entrou'} sem WhatsApp (sem telefone na Receita ou telefone repetido/já usado).
              Os telefones encontrados ficaram nas observações de cada cliente.</span>
            <Link to="/clientes?semWhatsapp=true">Completar cadastros →</Link>
          </div>
        )}
        {res.ignorados.length > 0 && (
          <div className="alert warn" style={{ flexDirection: 'column', gap: 6 }}>
            <strong>{res.ignorados.length} não {res.ignorados.length > 1 ? 'foram importadas' : 'foi importada'}:</strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {res.ignorados.slice(0, 8).map((i) => <li key={i.cnpj}>{i.nome ?? cnpjFmt(i.cnpj)} — {i.descricao}</li>)}
              {res.ignorados.length > 8 && <li>e mais {res.ignorados.length - 8}…</li>}
            </ul>
          </div>
        )}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {res.importados.length === 1 && <Link className="btn" to={`/clientes/${res.importados[0]!.clienteId}`}>Abrir cliente</Link>}
          {res.importados.length > 1 && <Link className="btn" to="/clientes?status=PROSPECT">Ver na carteira</Link>}
          <button className="btn primary" onClick={onClose}>Continuar prospectando</button>
        </div>
      </Modal>
    );
  }

  const excede = livres !== null && pedidas > livres;
  const titulo = alvo.tipo === 'cnpjs' && pedidas === 1 ? (alvo.nomes.get(alvo.cnpjs[0]!) ?? '1 empresa') : `${num(n)} empresas`;

  return (
    <Modal titulo="Importar para a carteira" onClose={onClose}>
      <div className="col" style={{ gap: 4 }}>
        <h2 style={{ fontSize: 18 }}>Importar {titulo} para a carteira</h2>
        <span className="muted small">
          Entram como clientes “Prospect”. Quem já está na carteira ou é NÃO CONTACTAR fica de fora.
          Sem telefone (ou com telefone repetido) entra sem WhatsApp, para você completar depois.
        </span>
      </div>

      {podeDistribuir ? (
        <fieldset className="col" style={{ border: 0, padding: 0, margin: 0, gap: 8 }}>
          <legend className="small muted" style={{ marginBottom: 8 }}>Responsável</legend>
          <label className={`opcao${distribuicao === 'EU' ? ' on' : ''}`}><input type="radio" name="dist" checked={distribuicao === 'EU'} onChange={() => setDistribuicao('EU')} />Atribuir a mim ({me?.usuario.nome})</label>
          <label className={`opcao${distribuicao === 'RODIZIO' ? ' on' : ''}`}><input type="radio" name="dist" checked={distribuicao === 'RODIZIO'} onChange={() => setDistribuicao('RODIZIO')} />Distribuir em rodízio entre vendedores</label>
          {distribuicao === 'RODIZIO' && (
            <div className="col" style={{ gap: 8, paddingLeft: 26 }}>
              {usuarios.isLoading && <span className="small muted">Carregando…</span>}
              {ativos.map((u) => {
                const on = marcados.includes(u.id);
                const idx = marcados.indexOf(u.id);
                const qtd = on ? Math.floor(n / marcados.length) + (idx < n % marcados.length ? 1 : 0) : 0;
                return (
                  <label key={u.id} className="check">
                    <input type="checkbox" checked={on} onChange={() => setVendedores(on ? marcados.filter((x) => x !== u.id) : [...marcados, u.id])} />
                    {u.nome}{u.perfil === 'ADMIN' ? ' (admin)' : ''}
                    <span className="num faint" style={{ marginLeft: 'auto' }}>{on ? `+${qtd}` : ''}</span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>
      ) : (
        <span className="small muted">As empresas entram na sua carteira.</span>
      )}

      {podeDistribuir && (
        <fieldset className="col" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
          <legend className="small muted" style={{ marginBottom: 8 }}>Também atendem (opcional)</legend>
          <div className="lista-check">
            {ativos.map((u) => {
              const on = adicionais.includes(u.id);
              const bloqueado = !on && adicionais.length >= MAX_VENDEDORES_ADICIONAIS;
              return (
                <label key={u.id} className={`check${bloqueado ? ' off' : ''}`}>
                  <input type="checkbox" checked={on} disabled={bloqueado} onChange={() => setAdicionais(on ? adicionais.filter((x) => x !== u.id) : [...adicionais, u.id])} />
                  {u.nome}{u.perfil === 'ADMIN' ? ' (admin)' : ''}
                </label>
              );
            })}
          </div>
          <span className="small faint">Entram em todas as empresas importadas ({adicionais.length} de {MAX_VENDEDORES_ADICIONAIS}). Quem é principal de uma empresa não se repete como adicional dela.</span>
          {adicionais.length > 0 && n > 0 && (
            <div className="mini" aria-label="Como fica">
              <span className="l">Como fica</span>
              {(distribuicao === 'RODIZIO' ? marcados : [me!.usuario.id]).map((p, i, todos) => {
                const qtd = Math.floor(n / todos.length) + (i < n % todos.length ? 1 : 0);
                const extras = adicionais.filter((a) => a !== p).map(nomeDe);
                return qtd > 0 ? (
                  <span key={p} className="small"><strong>{nomeDe(p)}</strong>: {num(qtd)} {qtd === 1 ? 'empresa' : 'empresas'} · também atendem: {extras.length ? extras.join(', ') : '—'}</span>
                ) : null;
              })}
            </div>
          )}
        </fieldset>
      )}

      {(tags.data?.length ?? 0) > 0 && (
        <div className="field">
          <span>Tags (opcional)</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {tags.data!.map((t) => {
              const on = tagIds.includes(t.id);
              return (
                <button key={t.id} type="button" className={`pill${on ? ' on' : ''}`} aria-pressed={on} onClick={() => setTagIds(on ? tagIds.filter((x) => x !== t.id) : [...tagIds, t.id])}>
                  <TagChip nome={t.nome} cor={t.cor} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mini">
        <span className="l">Origem do contato (LGPD)</span>
        <span>Base pública CNPJ · Receita Federal</span>
        <span className="small faint">Base legal: legítimo interesse (prospecção B2B). Registrada em cada cliente com data e usuário; o opt-out continua valendo.</span>
      </div>

      {livres !== null && (
        <div className="col" style={{ gap: 6 }}>
          <div className="small" style={{ display: 'flex' }}><span className="muted">Espaço no plano</span><span className="num" style={{ marginLeft: 'auto' }}>{num(livres)} livres · usando até {num(n)}</span></div>
          <Progresso valor={Math.min(n, livres)} total={Math.max(livres, 1)} />
          {excede && (
            <span className="small" style={{ color: 'var(--amber-text)' }}>
              {alvo.tipo === 'filtro'
                ? `A busca tem ${num(pedidas)} empresas e o plano tem espaço para ${num(livres!)}: entram as ${num(livres!)} abertas mais recentemente.`
                : `A seleção passa do limite do plano: entram as primeiras ${num(livres!)}.`}
            </span>
          )}
        </div>
      )}

      {m.error && <Erro erro={mensagemErro(m.error)} />}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={m.isPending || (distribuicao === 'RODIZIO' && marcados.length === 0)} onClick={() => m.mutate()}>
          {m.isPending ? 'Importando…' : `Importar ${n === 1 ? 'empresa' : `${num(n)} empresas`}`}
        </button>
      </div>
    </Modal>
  );
}
